import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  CalendarIcon,
  Plus,
  Loader2,
  Receipt,
  Pencil,
  Trash2,
  Banknote,
  Shield,
  Building2,
  TrendingDown,
  CheckCircle2,
  Clock,
  Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const ExpensesPage = () => {
  const { isAdmin, isManager, user } = useAuth();
  const { stores, selectedStore } = useStore();
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [expenses, setExpenses] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [sourceBalances, setSourceBalances] = useState({ cash_holding: 0, safe_balance: 0, bank_accounts: [] });
  
  // Check if user is staff
  const isStaff = user?.user_type === 'staff';
  const canDelete = isAdmin || isManager;
  
  // Dialogs
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [expenseForm, setExpenseForm] = useState({
    date: new Date(),
    expense_type_id: '',
    expense_type_name: '',
    amount: '',
    description: '',
    payment_source: 'cash_holding',
    bank_account_id: ''
  });

  // Initialize store
  useEffect(() => {
    if (selectedStore?.id) {
      setSelectedStoreId(selectedStore.id);
      setBankAccounts(selectedStore.bank_accounts || []);
    }
  }, [selectedStore]);

  // Fetch data when store changes
  useEffect(() => {
    if (selectedStoreId) {
      fetchAllData();
      fetchSourceBalances();
    }
  }, [selectedStoreId]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchExpenses(),
        fetchExpenseTypes(),
        fetchSummary()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenses = async () => {
    try {
      const response = await axios.get(`${API}/api/expenses?store_id=${selectedStoreId}`);
      setExpenses(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  };

  const fetchExpenseTypes = async () => {
    try {
      const response = await axios.get(`${API}/api/expenses/types`);
      setExpenseTypes(response.data);
    } catch (error) {
      console.error('Error fetching expense types:', error);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/api/expenses/summary?store_id=${selectedStoreId}`);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const fetchSourceBalances = async () => {
    try {
      const response = await axios.get(`${API}/api/expenses/source-balances?store_id=${selectedStoreId}`);
      setSourceBalances(response.data);
      setBankAccounts(response.data.bank_accounts || []);
    } catch (error) {
      console.error('Error fetching source balances:', error);
    }
  };

  const openExpenseDialog = (expense = null) => {
    // Fetch latest balances when opening dialog
    fetchSourceBalances();
    if (expense) {
      setEditingExpense(expense);
      setExpenseForm({
        date: new Date(expense.date),
        expense_type_id: expense.expense_type_id,
        expense_type_name: expense.expense_type_name,
        amount: expense.amount.toString(),
        description: expense.description || '',
        payment_source: expense.payment_source,
        bank_account_id: expense.bank_account_id || ''
      });
    } else {
      setEditingExpense(null);
      setExpenseForm({
        date: new Date(),
        expense_type_id: '',
        expense_type_name: '',
        amount: '',
        description: '',
        payment_source: 'cash_holding',
        bank_account_id: ''
      });
    }
    setExpenseDialogOpen(true);
  };

  const handleExpenseSubmit = async () => {
    if (!expenseForm.expense_type_id || !expenseForm.amount) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        store_id: selectedStoreId,
        expense_type_id: expenseForm.expense_type_id,
        expense_type_name: expenseForm.expense_type_name,
        date: format(expenseForm.date, 'yyyy-MM-dd'),
        amount: parseFloat(expenseForm.amount),
        description: expenseForm.description,
        payment_source: expenseForm.payment_source,
        bank_account_id: expenseForm.payment_source === 'bank' ? expenseForm.bank_account_id : null
      };

      if (editingExpense) {
        await axios.put(`${API}/api/expenses/${editingExpense.id}`, payload);
        toast.success('Expense updated!');
      } else {
        await axios.post(`${API}/api/expenses`, payload);
        toast.success('Expense recorded!');
      }

      setExpenseDialogOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;

    try {
      await axios.delete(`${API}/api/expenses/${expenseId}`);
      toast.success('Expense deleted!');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete expense');
    }
  };

  const handleExpenseTypeSelect = (typeId) => {
    const type = expenseTypes.find(t => t.id === typeId);
    setExpenseForm({
      ...expenseForm,
      expense_type_id: typeId,
      expense_type_name: type?.name || ''
    });
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'cash_holding': return <Banknote className="h-4 w-4 text-green-400" />;
      case 'safe': return <Shield className="h-4 w-4 text-amber-400" />;
      case 'bank': return <Building2 className="h-4 w-4 text-blue-400" />;
      default: return null;
    }
  };

  const getSourceLabel = (source) => {
    switch (source) {
      case 'cash_holding': return 'Cash';
      case 'safe': return 'Safe';
      case 'bank': return 'Bank';
      default: return source;
    }
  };

  // For staff, find their most recent expense
  const staffLastExpense = isStaff 
    ? expenses.find(e => e.created_by === user?.user_id)
    : null;

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            Expenses
          </h1>
          <p className="text-muted-foreground">Track business expenses and payments</p>
        </div>
        <Button onClick={() => openExpenseDialog()} data-testid="new-expense-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Expense
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-hover border-red-500/20">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Expenses</p>
                <p className="text-2xl font-bold font-mono text-red-400">
                  £{(summary?.total_expenses || 0).toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {summary?.total_count || 0} transaction(s)
                </p>
              </div>
              <div className="p-3 rounded-xl bg-red-500/20">
                <TrendingDown className="h-6 w-6 text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover border-green-500/20">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">From Cash</p>
                <p className="text-2xl font-bold font-mono text-green-400">
                  £{(summary?.by_source?.cash_holding?.total || 0).toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {summary?.by_source?.cash_holding?.count || 0} transaction(s)
                </p>
              </div>
              <div className="p-3 rounded-xl bg-green-500/20">
                <Banknote className="h-6 w-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover border-blue-500/20">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">From Bank</p>
                <p className="text-2xl font-bold font-mono text-blue-400">
                  £{(summary?.by_source?.bank?.total || 0).toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {summary?.by_source?.bank?.count || 0} transaction(s)
                </p>
              </div>
              <div className="p-3 rounded-xl bg-blue-500/20">
                <Building2 className="h-6 w-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover border-purple-500/20">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Bank Reconciled</p>
                <p className="text-2xl font-bold font-mono text-purple-400">
                  £{(summary?.reconciliation?.reconciled?.total || 0).toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {summary?.reconciliation?.unreconciled?.count || 0} pending
                </p>
              </div>
              <div className="p-3 rounded-xl bg-purple-500/20">
                <CheckCircle2 className="h-6 w-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Expenses by Category</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {summary?.by_type?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                  <Receipt className="h-12 w-12 mb-4 opacity-50" />
                  <p>No expenses recorded</p>
                  <Button variant="outline" className="mt-4" onClick={() => openExpenseDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Record First Expense
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left p-4 font-medium text-muted-foreground">Category</th>
                        <th className="text-right p-4 font-medium text-muted-foreground">Count</th>
                        <th className="text-right p-4 font-medium text-muted-foreground">Total Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary?.by_type?.map((item) => (
                        <tr key={item.expense_type_id} className="border-b border-border/50 table-row-hover">
                          <td className="p-4 font-medium">{item.expense_type_name}</td>
                          <td className="p-4 text-right text-muted-foreground">{item.count}</td>
                          <td className="p-4 text-right font-mono font-semibold text-red-400">
                            £{item.total_amount.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">All Expenses</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {expenses.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                  <Receipt className="h-12 w-12 mb-4 opacity-50" />
                  <p>No expenses recorded</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left p-4 font-medium text-muted-foreground">ID</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Date</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Category</th>
                        <th className="text-left p-4 font-medium text-muted-foreground">Description</th>
                        <th className="text-center p-4 font-medium text-muted-foreground">Source</th>
                        <th className="text-center p-4 font-medium text-muted-foreground">Status</th>
                        <th className="text-right p-4 font-medium text-muted-foreground">Amount</th>
                        <th className="text-right p-4 font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.slice(0, 50).map((expense) => {
                        const canEdit = isStaff 
                          ? (staffLastExpense && expense.id === staffLastExpense.id && !expense.is_reconciled)
                          : !expense.is_reconciled;
                        
                        return (
                          <tr 
                            key={expense.id} 
                            className="border-b border-border/50 table-row-hover"
                            data-testid={`expense-row-${expense.id}`}
                          >
                            <td className="p-4 font-mono text-xs text-primary">
                              {expense.transaction_id || '-'}
                            </td>
                            <td className="p-4">
                              {new Date(expense.date).toLocaleDateString('en-GB')}
                            </td>
                            <td className="p-4 font-medium">{expense.expense_type_name}</td>
                            <td className="p-4 text-muted-foreground max-w-xs truncate">
                              {expense.description || '-'}
                            </td>
                            <td className="p-4">
                              <div className="flex items-center justify-center gap-1">
                                {getSourceIcon(expense.payment_source)}
                                <span className="text-xs">{getSourceLabel(expense.payment_source)}</span>
                              </div>
                            </td>
                            <td className="p-4 text-center">
                              {expense.payment_source === 'bank' && (
                                expense.is_reconciled ? (
                                  <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400">
                                    Reconciled
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 rounded-full text-xs bg-amber-500/20 text-amber-400">
                                    Pending
                                  </span>
                                )
                              )}
                            </td>
                            <td className="p-4 text-right font-mono font-semibold text-red-400">
                              £{expense.amount.toFixed(2)}
                            </td>
                            <td className="p-4 text-right">
                              <div className="flex items-center justify-end gap-1">
                                {canEdit && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => openExpenseDialog(expense)}
                                    data-testid={`edit-expense-${expense.id}`}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                )}
                                {canDelete && !expense.is_reconciled && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleDeleteExpense(expense.id)}
                                    className="text-red-400 hover:text-red-300"
                                    data-testid={`delete-expense-${expense.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                                {!canEdit && !canDelete && (
                                  <span className="text-xs text-muted-foreground">-</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New/Edit Expense Dialog */}
      <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingExpense ? 'Edit Expense' : 'New Expense'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Source Balance Info Card - AT THE TOP */}
            <div className="p-3 bg-muted/30 rounded-lg border">
              <div className="text-xs font-medium text-muted-foreground mb-2">Available Balances</div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className={`p-2 rounded ${expenseForm.payment_source === 'cash_holding' ? 'bg-green-500/10 border border-green-500/30' : 'bg-background'}`}>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Banknote className="h-3 w-3" /> Cash Holding
                  </div>
                  <div className="font-mono font-medium">£{sourceBalances.cash_holding?.toFixed(2) || '0.00'}</div>
                </div>
                <div className={`p-2 rounded ${expenseForm.payment_source === 'safe' ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-background'}`}>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Shield className="h-3 w-3" /> Safe
                  </div>
                  <div className="font-mono font-medium">£{sourceBalances.safe_balance?.toFixed(2) || '0.00'}</div>
                </div>
                <div className={`p-2 rounded ${expenseForm.payment_source === 'bank' ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-background'}`}>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Building2 className="h-3 w-3" /> Bank
                  </div>
                  <div className="font-mono font-medium">
                    £{sourceBalances.bank_accounts?.reduce((sum, acc) => sum + (acc.balance || 0), 0).toFixed(2) || '0.00'}
                  </div>
                </div>
              </div>
            </div>

            {/* Date & Category Row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start h-9 text-sm">
                      <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                      {format(expenseForm.date, 'dd/MM/yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={expenseForm.date}
                      onSelect={(date) => date && setExpenseForm({ ...expenseForm, date })}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Category *</Label>
                <Select 
                  value={expenseForm.expense_type_id}
                  onValueChange={handleExpenseTypeSelect}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseTypes.map(type => (
                      <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Amount & Payment Source Row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Amount (£) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                  className="font-mono h-9"
                  placeholder="0.00"
                  data-testid="expense-amount"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Paid From *</Label>
                <Select 
                  value={expenseForm.payment_source}
                  onValueChange={(value) => setExpenseForm({ ...expenseForm, payment_source: value, bank_account_id: '' })}
                  disabled={isStaff}
                >
                  <SelectTrigger className="h-9 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash_holding">
                      <div className="flex items-center gap-2">
                        <Banknote className="h-3.5 w-3.5 text-green-400" />
                        <span>Cash Holding</span>
                      </div>
                    </SelectItem>
                    {!isStaff && (
                      <>
                        <SelectItem value="safe">
                          <div className="flex items-center gap-2">
                            <Shield className="h-3.5 w-3.5 text-amber-400" />
                            <span>Safe</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="bank">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-3.5 w-3.5 text-blue-400" />
                            <span>Bank Account</span>
                          </div>
                        </SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Bank Account Selection (if paid from bank) */}
            {expenseForm.payment_source === 'bank' && bankAccounts.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs">Bank Account</Label>
                <Select 
                  value={expenseForm.bank_account_id}
                  onValueChange={(value) => setExpenseForm({ ...expenseForm, bank_account_id: value })}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select bank account" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.name} - £{(acc.balance || 0).toFixed(2)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Description */}
            <div className="space-y-1">
              <Label className="text-xs">Description / Reference</Label>
              <Input
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                placeholder="e.g., Invoice #123, Week 45 wages..."
                className="h-9"
                data-testid="expense-description"
              />
            </div>
          </div>

          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setExpenseDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleExpenseSubmit} disabled={saving} data-testid="save-expense-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingExpense ? 'Save Changes' : 'Record Expense'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpensesPage;
